// Store for managing visual scenario builder state
// Handles the drag-and-drop canvas, nodes, connections, and preview functionality

const state = {
  // Canvas state
  nodes: [],
  connections: [],
  selectedNodeId: null,

  // UI state
  showPreview: false,
  isLoading: false,
  error: null,

  // Configuration
  availableTools: [],
  visualConfig: null,

  // Preview state
  liquidPreview: '',
  previewError: null,
}

const getters = {
  selectedNode: (state) => {
    return state.nodes.find(node => node.id === state.selectedNodeId) || null
  },

  visualConfigJson: (state) => ({
    nodes: state.nodes.map(node => ({
      id: node.id,
      type: node.type,
      data: node.data,
      position: { x: node.x, y: node.y }
    })),
    edges: state.connections.map(conn => ({
      id: conn.id,
      source: conn.source,
      target: conn.target,
      label: conn.label
    })),
    start_node: state.nodes.length > 0 ? state.nodes[0].id : null
  }),

  isValidFlow: (state) => {
    // Basic validation: at least one node, no orphaned connections
    if (state.nodes.length === 0) return false

    const nodeIds = state.nodes.map(n => n.id)
    return state.connections.every(conn =>
      nodeIds.includes(conn.source) && nodeIds.includes(conn.target)
    )
  }
}

const mutations = {
  // Node mutations
  ADD_NODE(state, node) {
    state.nodes.push(node)
  },

  UPDATE_NODE(state, { nodeId, updates }) {
    const nodeIndex = state.nodes.findIndex(n => n.id === nodeId)
    if (nodeIndex !== -1) {
      state.nodes[nodeIndex] = { ...state.nodes[nodeIndex], ...updates }
    }
  },

  DELETE_NODE(state, nodeId) {
    state.nodes = state.nodes.filter(n => n.id !== nodeId)
    state.connections = state.connections.filter(
      conn => conn.source !== nodeId && conn.target !== nodeId
    )
    if (state.selectedNodeId === nodeId) {
      state.selectedNodeId = null
    }
  },

  SET_NODES(state, nodes) {
    state.nodes = nodes
  },

  // Connection mutations
  ADD_CONNECTION(state, connection) {
    // Prevent duplicate connections
    const existing = state.connections.find(
      conn => conn.source === connection.source && conn.target === connection.target
    )
    if (!existing) {
      state.connections.push(connection)
    }
  },

  DELETE_CONNECTION(state, connectionId) {
    state.connections = state.connections.filter(conn => conn.id !== connectionId)
  },

  SET_CONNECTIONS(state, connections) {
    state.connections = connections
  },

  // Selection mutations
  SELECT_NODE(state, nodeId) {
    state.selectedNodeId = nodeId
  },

  CLEAR_SELECTION(state) {
    state.selectedNodeId = null
  },

  // UI mutations
  SET_SHOW_PREVIEW(state, show) {
    state.showPreview = show
  },

  SET_LOADING(state, loading) {
    state.isLoading = loading
  },

  SET_ERROR(state, error) {
    state.error = error
  },

  // Configuration mutations
  SET_AVAILABLE_TOOLS(state, tools) {
    state.availableTools = tools
  },

  SET_VISUAL_CONFIG(state, config) {
    state.visualConfig = config
  },

  // Preview mutations
  SET_LIQUID_PREVIEW(state, preview) {
    state.liquidPreview = preview
  },

  SET_PREVIEW_ERROR(state, error) {
    state.previewError = error
  },

  // Canvas operations
  CLEAR_CANVAS(state) {
    state.nodes = []
    state.connections = []
    state.selectedNodeId = null
    state.visualConfig = null
    state.liquidPreview = ''
    state.previewError = null
  },

  LOAD_SCENARIO(state, scenario) {
    if (scenario && scenario.visual_config) {
      const config = scenario.visual_config

      // Load nodes
      state.nodes = config.nodes.map(node => ({
        id: node.id,
        type: node.type,
        data: node.data,
        x: node.position.x,
        y: node.position.y
      }))

      // Load connections
      state.connections = config.edges.map(edge => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        label: edge.label
      }))
    } else {
      state.nodes = []
      state.connections = []
    }

    state.selectedNodeId = null
    state.visualConfig = scenario?.visual_config || null
    state.liquidPreview = ''
    state.previewError = null
  }
}

const actions = {
  // Node actions
  addNode({ commit }, node) {
    commit('ADD_NODE', node)
  },

  updateNode({ commit }, { nodeId, updates }) {
    commit('UPDATE_NODE', { nodeId, updates })
  },

  deleteNode({ commit }, nodeId) {
    commit('DELETE_NODE', nodeId)
  },

  selectNode({ commit }, nodeId) {
    commit('SELECT_NODE', nodeId)
  },

  clearSelection({ commit }) {
    commit('CLEAR_SELECTION')
  },

  // Connection actions
  addConnection({ commit }, connection) {
    commit('ADD_CONNECTION', connection)
  },

  deleteConnection({ commit }, connectionId) {
    commit('DELETE_CONNECTION', connectionId)
  },

  // Canvas actions
  clearCanvas({ commit }) {
    commit('CLEAR_CANVAS')
  },

  loadScenario({ commit }, scenario) {
    commit('LOAD_SCENARIO', scenario)
  },

  // Preview actions
  async generatePreview({ commit, getters }) {
    commit('SET_LOADING', true)
    commit('SET_PREVIEW_ERROR', null)

    try {
      const visualConfig = getters.visualConfigJson

      // Call API to generate preview
      const response = await fetch('/api/v1/accounts/1/captain/scenarios/1/preview_liquid', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        },
        body: JSON.stringify({
          visual_config: visualConfig
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      if (data.success) {
        commit('SET_LIQUID_PREVIEW', data.liquid_template)
      } else {
        throw new Error(data.error || 'Error desconocido')
      }
    } catch (error) {
      commit('SET_PREVIEW_ERROR', error.message)
      commit('SET_LIQUID_PREVIEW', '')
    } finally {
      commit('SET_LOADING', false)
    }
  },

  showPreview({ commit }) {
    commit('SET_SHOW_PREVIEW', true)
  },

  hidePreview({ commit }) {
    commit('SET_SHOW_PREVIEW', false)
  },

  // Tool actions
  setAvailableTools({ commit }, tools) {
    commit('SET_AVAILABLE_TOOLS', tools)
  },

  // Save actions
  async saveScenario({ commit, getters }, { scenarioId, assistantId }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const visualConfig = getters.visualConfigJson

      const response = await fetch(`/api/v1/accounts/1/captain/assistants/${assistantId}/scenarios/${scenarioId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        },
        body: JSON.stringify({
          scenario: {
            visual_config: visualConfig,
            editor_mode: 'visual'
          }
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      commit('SET_ERROR', error.message)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
